import React, { useMemo, useState } from 'react';
import { ReactFlow, BaseEdge, Handle, Position, Edge, EdgeProps, Node, NodeProps, MarkerType } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import GavelIcon from '@mui/icons-material/Gavel';
import {
    ActionsResponse, GateActions, PipelineNode, PipelineResponse, QueueActions
} from '../../services/caseWorkflowService';
import { LayoutableEdge, LayoutableNode, layoutWithDagre } from '../../utils/dagreLayout';
import { splitTrailingParenthetical } from '../../utils/textFormat';
import { KeySummary } from '../ExecutionStats/useExecutionSummary';
import { ATTENTION_SHORT, OPERATIONS_BUCKETS, attentionInk } from '../ExecutionStats/executionStatsVocabulary';
import { PIPELINE_CAPTION, queueShort } from './projectOutcomesVocabulary';
import { dsVar, ink } from '../../theme/tokens';

// Drawn at 100%, never zoomed to fit: fitting nine columns into a ~850px content
// column shrank 12px text to about 8px. Stage rows keep it within the width instead.
const WORKFLOW_W = 184;
const WORKFLOW_H = 76;
const GATE_W = 132;
const GATE_H = 64;
const BAND_H = 26;
const BAND_GAP = 12;
/** A stage label's width from its length: monospace, uppercase, letter-spaced. */
const BAND_CHAR_W = 8;
/**
 * Room each arrow label is given in the layout. Estimated from its length, as
 * nothing is measured before layout; the slack covers its background padding.
 * The layout keeps half a rank gap (20px) either side, so an estimate a little
 * short still clears the boxes.
 */
const LABEL_CHAR_W = 6.5;
const LABEL_SLACK = 16;
const LABEL_H = 22;
/** Stages stack as rows; arrows between rows run along tracks in the gap below their source row. */
const ROW_GAP = 32;
const GUTTER_PAD = 10;
const TRACK_H = 28;
/** How far an arrow between rows runs out of its source, and into its target, before turning. */
const STUB = 20;
const CORNER = 6;
/** Space kept around the drawing inside its scroll box. */
const PAD = 12;
const RUN_WINDOW = '30d' as const;

// Hex, not a token: it is alpha-suffixed for the waiting tint (`${ACCENT}14`).
const ACCENT = '#FFDE59';
const SURFACE = dsVar('surface');
const LINE = dsVar('line');
const MUTED = dsVar('fg-muted');
const HANDOFF = dsVar('info');

interface WorkflowData extends Record<string, unknown> {
    node: PipelineNode;
    runChips: { label: string; ink: string }[];
}

interface GateData extends Record<string, unknown> {
    code: string;
    label: string;
    gate: GateActions | null;
}

interface BandData extends Record<string, unknown> {
    label: string;
}

/** One row of the table view: an arrow and what is on it. */
export interface PipelineRow {
    from: string;
    to: string;
    kind: 'handoff' | 'feed';
    gates: string;
    queues: string;
}

const workflowId = (flowCode: string) => `flow:${flowCode}`;
const gateId = (raisingFlow: string, gateCode: string) => `gate:${raisingFlow}:${gateCode}`;

const nameOf = (node: PipelineNode | undefined, fallback: string) =>
    node?.campaignName || fallback.replace(/_/g, ' ').toLowerCase();

/** "6 ready for SAR · 7 held for EDD sign-off", or null when nothing is queued. */
const queueText = (codes: string[], queues: Map<string, QueueActions>): { text: string; total: number } | null => {
    if (!codes.length) return null;
    const parts = codes.map((code) => {
        const queue = queues.get(code);
        return { n: queue?.cases ?? 0, label: queueShort(code, queue?.label) };
    });
    return {
        text: parts.map((p) => `${p.n.toLocaleString()} ${p.label}`).join(' · '),
        total: parts.reduce((sum, p) => sum + p.n, 0)
    };
};

/** The room an arrow's label needs, so the layout leaves it a column of its own. */
const labelSize = (edge: LayoutableEdge) => {
    const label = (edge as Edge).label;
    return typeof label === 'string'
        ? { width: Math.ceil(label.length * LABEL_CHAR_W) + LABEL_SLACK, height: LABEL_H }
        : undefined;
};

const sizeOf = (node: LayoutableNode) => (node.id.startsWith('gate:') ? { width: GATE_W, height: GATE_H } : undefined);

const layout = (nodes: Node[], edges: LayoutableEdge[], withLabels: boolean) => layoutWithDagre(nodes, edges, {
    nodeWidth: WORKFLOW_W,
    nodeHeight: WORKFLOW_H,
    rankdir: 'LR',
    nodesep: 24,
    ranksep: 40,
    // A gate gets a column of its own width, not a workflow's.
    sizeOf,
    labelSizeOf: withLabels ? labelSize : undefined
});

/**
 * Keeps a row in pipeline order where its boxes are linked only through other
 * rows: a box with no arrow in from its own row follows the column before it.
 * These steer the layout and are never drawn.
 */
const orderingEdges = (members: Node[], inRow: Edge[], column: (id: string) => number): LayoutableEdge[] => {
    const columns = [...new Set(members.map((n) => column(n.id)))]
        .sort((a, b) => a - b)
        .map((at) => members.filter((n) => column(n.id) === at));
    const fed = new Set(inRow.map((e) => e.target));
    return columns.slice(1).flatMap((boxes, i) => boxes
        .filter((n) => !fed.has(n.id))
        .map((n) => ({ source: columns[i][0].id, target: n.id })));
};

/**
 * The map's nodes and edges: one row per stage, rows stacked in pipeline order,
 * each read left to right.
 *
 * A gate is a node of its own on the arrow from the flow that raises it, so dagre
 * gives it a column and the eye reads "flow → decision → flow". A gate on several
 * arrows (the EDD referral leads to EDD and to SAR preparation) is one node that
 * fans out. Queue counts label the arrow into the flow that consumes them, so each
 * count is drawn once.
 *
 * A stage is a run of adjacent workflows sharing a type, and is labelled as the
 * cards are; the order is the contract's. Rows rather than one long line, so the
 * map fits the page's width at 100% instead of scrolling or shrinking: each row
 * is laid out on its own, with room for its arrow labels. A gate joins the row of
 * the first workflow it leads to, read as the way into that stage. Arrows between
 * rows each get a track in the gap below their source row, which carries their
 * label clear of every box.
 */
export const buildPipelineGraph = (
    pipeline: PipelineResponse,
    actions: ActionsResponse | null,
    runStatsById: Record<string, KeySummary | null> = {}
): { nodes: Node[]; edges: Edge[]; rows: PipelineRow[]; marginLeft: number } => {
    const gates = new Map((actions?.gates ?? []).map((g) => [g.code, g]));
    const queues = new Map((actions?.queues ?? []).map((q) => [q.code, q]));
    const byFlow = new Map(pipeline.nodes.filter((n) => n.flowCode).map((n) => [n.flowCode as string, n]));

    const workflowNodes: Node[] = pipeline.nodes.filter((n) => n.flowCode).map((node) => {
        const counts = runStatsById[node.campaignId]?.counts[RUN_WINDOW];
        const runChips = OPERATIONS_BUCKETS
            .filter((bucket) => (counts?.[bucket] ?? 0) > 0)
            .map((bucket) => ({ label: `${counts?.[bucket]} ${ATTENTION_SHORT[bucket]}`, ink: attentionInk(bucket) }));
        return {
            id: workflowId(node.flowCode as string),
            type: 'workflow',
            position: { x: 0, y: 0 },
            data: { node, runChips } as WorkflowData,
            width: WORKFLOW_W,
            height: WORKFLOW_H
        };
    });

    const gateNodes = new Map<string, Node>();
    /** The workflow each gate is raised by: its row when it leads to none. */
    const raisedBy = new Map<string, string>();
    const edges = new Map<string, Edge>();
    const rows: PipelineRow[] = [];

    const addEdge = (source: string, target: string, extra: Partial<Edge> = {}) => {
        const id = `${source}->${target}`;
        if (!edges.has(id)) edges.set(id, { id, source, target, ...extra });
    };

    for (const flowEdge of pipeline.edges) {
        const source = workflowId(flowEdge.from);
        const target = workflowId(flowEdge.to);
        const queued = queueText(flowEdge.queues, queues);
        const consumer = byFlow.get(flowEdge.to);
        const feed = flowEdge.kind === 'feed';
        // Arrows in their own hue so they read apart from the box borders; one
        // with cases sitting on it takes the accent, like a gate with cases waiting.
        const stroke = feed ? MUTED : queued?.total ? dsVar('accent-fg') : HANDOFF;
        const arrow = {
            markerEnd: { type: MarkerType.ArrowClosed, color: stroke, width: 16, height: 16 },
            style: { stroke, strokeWidth: 2, ...(feed ? { strokeDasharray: '6 4' } : {}) },
            data: { consumerCampaignId: consumer?.campaignId ?? null }
        };
        const labelled = queued
            ? {
                label: queued.text,
                labelStyle: { fill: queued.total ? dsVar('fg-body') : dsVar('fg-2'), fontSize: 12, fontWeight: queued.total ? 600 : 400 },
                labelBgStyle: { fill: dsVar('page') },
                labelBgPadding: [6, 3] as [number, number],
                labelBgBorderRadius: 4
            }
            : feed
                ? {
                    label: flowEdge.label || 'feeds',
                    labelStyle: { fill: dsVar('fg-2'), fontSize: 12, fontStyle: 'italic' },
                    labelBgStyle: { fill: dsVar('page') },
                    labelBgPadding: [4, 2] as [number, number]
                }
                : {};

        // Route through each gate on the arrow, the last segment carrying the counts.
        let previous = source;
        for (const code of flowEdge.gates) {
            const id = gateId(flowEdge.from, code);
            if (!gateNodes.has(id)) {
                const gate = gates.get(code) ?? null;
                gateNodes.set(id, {
                    id,
                    type: 'gate',
                    position: { x: 0, y: 0 },
                    data: { code, label: gate?.label ?? code.replace(/_/g, ' ').toLowerCase(), gate } as GateData,
                    width: GATE_W,
                    height: GATE_H
                });
                raisedBy.set(id, source);
            }
            // Cases wait on the last segment only; the legs into the gates stay plain.
            addEdge(previous, id, { style: { ...arrow.style, stroke: feed ? MUTED : HANDOFF } });
            previous = id;
        }
        addEdge(previous, target, { ...arrow, ...labelled });

        rows.push({
            from: nameOf(byFlow.get(flowEdge.from), flowEdge.from),
            to: nameOf(consumer, flowEdge.to),
            kind: flowEdge.kind,
            gates: flowEdge.gates
                .map((code) => {
                    const gate = gates.get(code);
                    return gate ? `${gate.label} (${gate.openCases} waiting)` : code;
                })
                .join(', '),
            queues: queued?.text ?? ''
        });
    }

    const nodes = [...workflowNodes, ...gateNodes.values()];
    const allEdges = [...edges.values()];

    // The whole pipeline laid out once, for its left-to-right order only. Centres,
    // so a gate and a workflow in one column compare equal.
    const centreOf = new Map(layout(nodes, allEdges, false)
        .map((n) => [n.id, Math.round(n.position.x + (n.width ?? WORKFLOW_W) / 2)]));
    const column = (id: string) => centreOf.get(id) ?? 0;

    const stageOf = new Map<string, number>();
    const stageLabels: string[] = [];
    let lastType: string | null = null;
    [...workflowNodes].sort((a, b) => column(a.id) - column(b.id)).forEach((flow) => {
        const node = (flow.data as WorkflowData).node;
        const type = node.flowType ?? 'OTHER';
        if (type !== lastType) {
            stageLabels.push(node.campaignTypeName || node.campaignType || 'Other');
            lastType = type;
        }
        stageOf.set(flow.id, stageLabels.length - 1);
    });

    const targetsOf = new Map<string, string[]>();
    allEdges.forEach((e) => targetsOf.set(e.source, [...(targetsOf.get(e.source) ?? []), e.target]));
    // Through any further gates on the arrow, to the workflows it ends at.
    const workflowsAfter = (id: string, seen = new Set<string>()): string[] => {
        seen.add(id);
        return (targetsOf.get(id) ?? []).flatMap((target) => {
            if (target.startsWith('flow:')) return [target];
            return seen.has(target) ? [] : workflowsAfter(target, seen);
        });
    };
    gateNodes.forEach((_, id) => {
        const next = workflowsAfter(id).sort((a, b) => column(a) - column(b))[0];
        stageOf.set(id, stageOf.get(next ?? raisedBy.get(id) ?? '') ?? 0);
    });

    const stage = (id: string) => stageOf.get(id) ?? 0;
    const crossing = allEdges.filter((e) => stage(e.source) < stage(e.target));

    const placed = new Map<string, Node>();
    const bands: Node[] = [];
    const trackY = new Map<string, number>();
    let top = 0;
    stageLabels.forEach((label, row) => {
        const members = nodes.filter((n) => stage(n.id) === row);
        const ids = new Set(members.map((n) => n.id));
        const inRow = allEdges.filter((e) => ids.has(e.source) && ids.has(e.target));
        const laid = layout(members, [...inRow, ...orderingEdges(members, inRow, column)], true);
        const left = Math.min(...laid.map((n) => n.position.x));
        const above = Math.min(...laid.map((n) => n.position.y));
        const boxesTop = top + BAND_H + BAND_GAP;
        let bottom = boxesTop;
        laid.forEach((n) => {
            const y = n.position.y - above + boxesTop;
            placed.set(n.id, { ...n, position: { x: n.position.x - left, y } });
            bottom = Math.max(bottom, y + (n.height ?? WORKFLOW_H));
        });

        // Sized to its label rather than the row: an arrow into a box further
        // along the row turns down through the space above it.
        bands.push({
            id: `band:${row}`,
            type: 'band',
            position: { x: 0, y: top },
            data: { label } as BandData,
            style: { width: Math.ceil(label.length * BAND_CHAR_W) + 24, height: BAND_H },
            draggable: false,
            selectable: false,
            zIndex: -1
        });

        // The leftmost source takes the top track, so no arrow dropping to a
        // lower track crosses one running above it.
        const leaving = crossing
            .filter((e) => ids.has(e.source))
            .sort((a, b) => (placed.get(a.source)!.position.x - placed.get(b.source)!.position.x)
                || (column(a.target) - column(b.target)));
        leaving.forEach((e, i) => trackY.set(e.id, bottom + GUTTER_PAD + i * TRACK_H + TRACK_H / 2));
        top = bottom + Math.max(ROW_GAP, 2 * GUTTER_PAD + leaving.length * TRACK_H);
    });

    return {
        nodes: [...bands, ...nodes.map((n) => placed.get(n.id) ?? n)],
        edges: allEdges.map((e) => {
            const track = trackY.get(e.id);
            return track === undefined ? e : { ...e, type: 'stage', data: { ...e.data, trackY: track } };
        }),
        rows,
        // An arrow into a row's first box turns down just left of it.
        marginLeft: trackY.size ? STUB + 4 : 0
    };
};

/* ------------------------------------------------------------------ nodes */

const hidden = { opacity: 0, width: 1, height: 1, minWidth: 0, minHeight: 0, border: 'none' };

const WorkflowNode: React.FC<NodeProps> = ({ data }) => {
    const { node, runChips } = data as WorkflowData;
    const older = node.alsoCampaignIds?.length ?? 0;
    // The role in brackets is dropped to keep the box to its title; hover shows it.
    const { primary: title } = splitTrailingParenthetical(node.campaignName ?? '');
    return (
        <div
            role="button"
            aria-label={`Open workflow ${node.campaignName}`}
            className="h-full rounded-xl border px-3 py-2 text-left cursor-pointer transition-colors hover:border-accent-fg/70"
            style={{ width: WORKFLOW_W, height: WORKFLOW_H, background: SURFACE, borderColor: LINE }}
        >
            <Handle type="target" position={Position.Left} style={hidden} isConnectable={false} />
            <div className="text-[13px] leading-snug font-semibold text-fg line-clamp-2" title={node.campaignName ?? undefined}>{title || node.campaignName}</div>
            <div className="flex flex-wrap items-center gap-1 mt-1">
                {runChips.map((chip) => (
                    <span key={chip.label} className="rounded-full px-1.5 text-[11px] font-semibold" style={{ color: ink(chip.ink), border: `1px solid ${chip.ink}80` }}>
                        {chip.label}
                    </span>
                ))}
                {older > 0 && <span className="text-[11px] text-fg-2">+{older} older</span>}
            </div>
            <Handle type="source" position={Position.Right} style={hidden} isConnectable={false} />
        </div>
    );
};

const GateNode: React.FC<NodeProps> = ({ data }) => {
    const { label, gate } = data as GateData;
    const waiting = gate?.openCases ?? 0;
    return (
        <div
            role="button"
            aria-label={`${label}: ${waiting} ${waiting === 1 ? 'case' : 'cases'} waiting`}
            aria-disabled={!waiting}
            className={`rounded-lg border px-2 py-1.5 text-center ${waiting ? 'cursor-pointer' : 'cursor-default'}`}
            style={{
                width: GATE_W,
                height: GATE_H,
                background: waiting ? `${ACCENT}14` : dsVar('page'),
                borderColor: waiting ? `${ACCENT}99` : LINE
            }}
        >
            <Handle type="target" position={Position.Left} style={hidden} isConnectable={false} />
            <div className="flex items-center justify-center gap-1 text-[11px] font-semibold uppercase tracking-wide" style={{ color: waiting ? dsVar('accent-fg') : dsVar('fg-2') }}>
                <GavelIcon sx={{ fontSize: 12 }} />
                {label}
            </div>
            <div className={`text-sm font-bold ${waiting ? 'text-fg' : 'text-fg-2'}`}>
                {waiting ? `${waiting.toLocaleString()} waiting` : 'none waiting'}
            </div>
            {gate?.sarRecommended ? (
                <div className="text-[11px] text-fg-soft">{gate.sarRecommended} recommend SAR</div>
            ) : null}
            <Handle type="source" position={Position.Right} style={hidden} isConnectable={false} />
        </div>
    );
};

const BandNode: React.FC<NodeProps> = ({ data }) => (
    <div
        className="h-full rounded-md px-2.5 flex items-center whitespace-nowrap text-xs font-mono font-semibold uppercase tracking-wider text-fg-body"
        style={{ background: dsVar('raised'), border: `1px solid ${dsVar('line')}` }}
    >
        {(data as BandData).label}
    </div>
);

const NODE_TYPES = { workflow: WorkflowNode, gate: GateNode, band: BandNode };

/* ------------------------------------------------------------------ edges */

/** An SVG path through right-angled points, its corners rounded. */
export const orthogonalPath = (points: [number, number][], radius: number): string => {
    let d = `M ${points[0][0]} ${points[0][1]}`;
    for (let i = 1; i < points.length - 1; i++) {
        const [px, py] = points[i - 1];
        const [x, y] = points[i];
        const [nx, ny] = points[i + 1];
        // Never more than half a leg, so two corners on a short leg do not overlap.
        const r = Math.min(radius, Math.hypot(x - px, y - py) / 2, Math.hypot(nx - x, ny - y) / 2);
        d += ` L ${x - Math.sign(x - px) * r} ${y - Math.sign(y - py) * r}`;
        d += ` Q ${x} ${y} ${x + Math.sign(nx - x) * r} ${y + Math.sign(ny - y) * r}`;
    }
    const [lx, ly] = points[points.length - 1];
    return `${d} L ${lx} ${ly}`;
};

/**
 * An arrow from one row to a later one: out of its source, down to its track in
 * the gap below that row, along to just left of its target, and into it. The
 * label sits on the track, clear of every box.
 */
const StageEdge: React.FC<EdgeProps> = ({
    id, sourceX, sourceY, targetX, targetY, data, style, markerEnd,
    label, labelStyle, labelBgStyle, labelBgPadding, labelBgBorderRadius
}) => {
    const track = (data as { trackY: number }).trackY;
    const riseX = sourceX + STUB;
    const dropX = targetX - STUB;
    const path = orthogonalPath(
        [[sourceX, sourceY], [riseX, sourceY], [riseX, track], [dropX, track], [dropX, targetY], [targetX, targetY]],
        CORNER
    );
    return (
        <BaseEdge
            id={id}
            path={path}
            style={style}
            markerEnd={markerEnd}
            label={label}
            labelX={(riseX + dropX) / 2}
            labelY={track}
            labelStyle={labelStyle}
            labelShowBg={Boolean(label)}
            labelBgStyle={labelBgStyle}
            labelBgPadding={labelBgPadding}
            labelBgBorderRadius={labelBgBorderRadius}
        />
    );
};

const EDGE_TYPES = { stage: StageEdge };

/* -------------------------------------------------------------------- map */

/**
 * The drawing's size at 100%, and the offset that puts its top-left at PAD.
 * marginLeft is room left of the boxes, for arrows that turn down beside them.
 */
export const drawingBounds = (nodes: Node[], marginLeft = 0) => {
    if (!nodes.length) return { width: 0, height: 0, offsetX: PAD, offsetY: PAD };
    const widthOf = (n: Node) => n.width ?? Number((n.style as { width?: number } | undefined)?.width ?? 0);
    const heightOf = (n: Node) => n.height ?? Number((n.style as { height?: number } | undefined)?.height ?? 0);
    const minX = Math.min(...nodes.map((n) => n.position.x)) - marginLeft;
    const minY = Math.min(...nodes.map((n) => n.position.y));
    const maxX = Math.max(...nodes.map((n) => n.position.x + widthOf(n)));
    const maxY = Math.max(...nodes.map((n) => n.position.y + heightOf(n)));
    return {
        width: Math.ceil(maxX - minX + 2 * PAD),
        height: Math.ceil(maxY - minY + 2 * PAD),
        offsetX: PAD - minX,
        offsetY: PAD - minY
    };
};

interface PipelineMapProps {
    pipeline: PipelineResponse;
    actions: ActionsResponse | null;
    runStatsById?: Record<string, KeySummary | null>;
    onOpenWorkflow: (campaignId: string) => void;
    onOpenGate: (gate: GateActions) => void;
}

/**
 * The project's workflows in pipeline order, a row per stage, with the review
 * gates and work queues on the arrows between them.
 *
 * Replaces the decision and queue tiles: the same counts, placed where they sit in
 * the process. Read-only — nodes do not drag and nothing connects.
 *
 * Drawn at 100% in a box exactly the drawing's size. Stage rows keep it within
 * the column; a row that is still wider scrolls sideways rather than shrinking
 * its text, and the wheel still scrolls the page. Zooming to fit made a
 * nine-column pipeline unreadable.
 */
const PipelineMap: React.FC<PipelineMapProps> = ({ pipeline, actions, runStatsById, onOpenWorkflow, onOpenGate }) => {
    const [asTable, setAsTable] = useState(false);
    const { nodes, edges, rows, marginLeft } = useMemo(
        () => buildPipelineGraph(pipeline, actions, runStatsById),
        [pipeline, actions, runStatsById]
    );
    const bounds = useMemo(() => drawingBounds(nodes, marginLeft), [nodes, marginLeft]);

    return (
        <section aria-label="Workflow pipeline" className="text-left">
            <div className="flex items-center gap-2 mb-2">
                <AccountTreeIcon sx={{ fontSize: 16, color: dsVar('accent-fg') }} />
                <h3 className="text-sm font-semibold text-fg m-0">How work moves through this project</h3>
                <span className="text-xs text-fg-muted truncate flex-1 min-w-0">{PIPELINE_CAPTION}</span>
                <button
                    type="button"
                    aria-pressed={asTable}
                    onClick={() => setAsTable((v) => !v)}
                    className="flex-none bg-transparent border border-line rounded-md px-2 py-0.5 text-[11px] text-fg-soft cursor-pointer hover:border-accent-fg/60"
                >
                    {asTable ? 'Show map' : 'Show as table'}
                </button>
            </div>

            {asTable ? (
                <table className="w-full text-xs text-fg-soft border-collapse">
                    <thead>
                        <tr className="text-fg-muted text-left">
                            <th className="py-1 pr-3 font-medium">From</th>
                            <th className="py-1 pr-3 font-medium">To</th>
                            <th className="py-1 pr-3 font-medium">Decision on the way</th>
                            <th className="py-1 font-medium">Queued</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={`${row.from}>${row.to}`} className="border-t border-line">
                                <td className="py-1 pr-3">{row.from}</td>
                                <td className="py-1 pr-3">{row.to}{row.kind === 'feed' ? ' (data feed)' : ''}</td>
                                <td className="py-1 pr-3">{row.gates || '—'}</td>
                                <td className="py-1">{row.queues || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <div className="pipeline-map rounded-xl border border-line bg-page overflow-x-auto overflow-y-hidden">
                    <div style={{ width: bounds.width, height: bounds.height }}>
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            nodeTypes={NODE_TYPES}
                            edgeTypes={EDGE_TYPES}
                            colorMode="dark"
                            nodesDraggable={false}
                            nodesConnectable={false}
                            elementsSelectable={false}
                            panOnDrag={false}
                            zoomOnScroll={false}
                            zoomOnPinch={false}
                            zoomOnDoubleClick={false}
                            preventScrolling={false}
                            defaultViewport={{ x: bounds.offsetX, y: bounds.offsetY, zoom: 1 }}
                            proOptions={{ hideAttribution: true }}
                            style={{ background: 'transparent' }}
                            onNodeClick={(_, node) => {
                                if (node.type === 'workflow') onOpenWorkflow((node.data as WorkflowData).node.campaignId);
                                if (node.type === 'gate') {
                                    const gate = (node.data as GateData).gate;
                                    if (gate && gate.openCases > 0) onOpenGate(gate);
                                }
                            }}
                            onEdgeClick={(_, edge) => {
                                const campaignId = (edge.data as { consumerCampaignId?: string | null } | undefined)?.consumerCampaignId;
                                if (campaignId) onOpenWorkflow(campaignId);
                            }}
                        />
                    </div>
                </div>
            )}

            {pipeline.unconnected.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-2">
                    <span className="text-[11px] font-mono uppercase tracking-wider text-fg-muted">Other workflows</span>
                    {pipeline.unconnected.map((node) => (
                        <button
                            key={node.campaignId}
                            type="button"
                            onClick={() => onOpenWorkflow(node.campaignId)}
                            className="bg-transparent border border-line rounded-full px-2.5 py-0.5 text-[11px] text-fg-soft cursor-pointer hover:border-accent-fg/60"
                        >
                            {node.campaignName || node.campaignId}
                        </button>
                    ))}
                </div>
            )}
        </section>
    );
};

export default PipelineMap;
